import { CognitoUser } from "@aws-amplify/auth";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Link,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { usePostHog } from "@posthog/react";
import { convertToHubUser } from "contexts/identity-context";
import * as localStorageKeys from "data/local-storage-keys";
import { confirmSignUp, signIn } from "data/mutations";
import { Form, Formik } from "formik";
import queryString from "query-string";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useLocation } from "react-router";
import * as Routes from "routes";
import { InferType, boolean, object, string } from "yup";
import { useIdentity } from "../../contexts/identity-context";
import { MfaLogin } from "./mfa-login";

const minPasswordLength = 8;

export const SignInValidationSchema = object({
  email: string().email("Enter a valid email").required("Email is required"),
  password: string()
    .min(
      minPasswordLength,
      `Password should be of minimum ${minPasswordLength} characters length`
    )
    .required("Password is required"),
  termsOfUse: boolean(),
});

type SignInValues = InferType<typeof SignInValidationSchema>;

export const SignIn = () => {
  const [_, setUserState] = useIdentity();
  const { search } = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [isTermsOfUseRequired, setIsTermsOfUseRequired] = useState(false);
  const optOutMfa = localStorage.getItem(localStorageKeys.optOutMfa);
  const mfaSetupRequired = user && !user.challengeName && optOutMfa !== "true";
  const posthog = usePostHog();

  // Mutation for email confirmation from URL
  const confirmSignUpMutation = useMutation(
    async ({ username, code }: { username: string; code: string }) => {
      return await confirmSignUp(username, code);
    }
  );

  // Check URL params for confirmation code
  useEffect(() => {
    (async function () {
      const params = queryString.parse(search);
      if (params.code && params.username) {
        const username = String(params["username"]);
        const code = String(params["code"]);
        confirmSignUpMutation.mutate({ username, code });
      }
    })();
  }, [search]);

  // React Query mutation for converting cognito user to hub user
  const convertToHubUserMutation = useMutation(
    (cognitoUser: CognitoUser) => convertToHubUser(cognitoUser),
    {
      onSuccess: (user) => {
        setUserState(user);
        setMfaRequired(false);
      },
    }
  );

  // React Query mutation for sign-in
  const signInMutation = useMutation(
    async (values: SignInValues) => {
      return await signIn(values);
    },
    {
      onSuccess: (cognitoUser) => {
        setUser(cognitoUser);

        if (
          cognitoUser.challengeName === "SMS_MFA" ||
          cognitoUser.challengeName === "SOFTWARE_TOKEN_MFA"
        ) {
          setMfaRequired(true);
          return;
        }
        posthog.capture("user_signed_in");
        convertToHubUserMutation.mutate(cognitoUser);
      },
      onError: (error: any) => {
        if (error.message.includes("terms_of_use_failure")) {
          setIsTermsOfUseRequired(true);
        }
        posthog.capture("user_failed_signing_in");
      },
    }
  );

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const getErrorMessage = (error: any): string => {
    if (error.message.includes("terms_of_use_failure")) {
      return "Please accept Geoscape Hub's Terms of Use to continue.";
    } else if (error.code === "UserNotFoundException") {
      return "Invalid username or password. Please try again.";
    } else if (error.code === "NotAuthorizedException") {
      if (error.message.includes("disabled")) {
        return "Account disabled. Please contact support";
      } else {
        return "Invalid username or password. Please try again.";
      }
    } else if (error.code === "UserNotConfirmedException") {
      return "This account needs to be verified before signing in. Check your email or use the 'Resend Verification' form to send a new confirmation.";
    } else {
      console.error(`User sign-in failure: ${error.message}`);
      return "We encountered an unexpected error. Please try again in a few minutes or contact support";
    }
  };

  return (
    <>
      {!mfaRequired && !mfaSetupRequired && (
        <Formik<SignInValues>
          initialValues={{ email: "", password: "", termsOfUse: false }}
          onSubmit={(values) => signInMutation.mutate(values)}
          validationSchema={SignInValidationSchema}
        >
          {({
            values: { email, password, termsOfUse },
            handleChange,
            handleBlur,
            errors,
            touched,
          }) => (
            <Form id="signinForm">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: "100vw",
                }}
              >
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    minWidth: "340px",
                    maxWidth: "500px",
                    m: 2,
                  }}
                >
                  <CardHeader
                    titleTypographyProps={{ variant: "h5" }}
                    title="Sign into your account"
                  />
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      width: "100%",
                      gap: "16px",
                    }}
                  >
                    <TextField
                      id="email"
                      name="email"
                      label="Email"
                      type="text"
                      value={email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      disabled={signInMutation.isLoading}
                      fullWidth
                      sx={customStyle}
                      InputProps={{
                        sx: {
                          borderRadius: "8px",
                          height: "40px",
                          display: "flex",
                          justifyContent: "center",
                          alignContent: "center",
                          alignItems: "center",
                        },
                      }}
                    />
                    <TextField
                      id="password"
                      name="password"
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      disabled={signInMutation.isLoading}
                      fullWidth
                      sx={customStyle}
                      InputProps={{
                        sx: {
                          borderRadius: "8px",
                          height: "40px",
                        },
                        endAdornment: (
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        ),
                      }}
                    />

                    {isTermsOfUseRequired ? (
                      <>
                        <FormControlLabel
                          sx={{
                            display: "flex",
                            height: "38px",
                            margin: "0px",
                          }}
                          control={
                            <Checkbox
                              color="primary"
                              id="termsOfUse"
                              size="small"
                              name="termsOfUse"
                              checked={termsOfUse}
                              onChange={handleChange}
                            />
                          }
                          label={
                            <>
                              {"I agree to Geoscape Hub's "}
                              <a
                                href="https://geoscape.com.au/legal/geoscape-developer-terms"
                                target="_blank"
                                rel="noreferrer"
                                style={{ textDecoration: "none" }}
                              >
                                Terms of Use
                              </a>
                            </>
                          }
                        />
                      </>
                    ) : null}

                    {signInMutation.isLoading ||
                    confirmSignUpMutation.isLoading ? (
                      <Box width="100%" display="flex" justifyContent="center">
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        {confirmSignUpMutation.isSuccess && (
                          <Alert
                            variant="outlined"
                            severity="success"
                            sx={{
                              borderRadius: "8px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              alignSelf: "stretch",
                            }}
                          >
                            <Typography variant="body2">
                              Your account is successfully confirmed.
                            </Typography>
                          </Alert>
                        )}

                        {confirmSignUpMutation.isError && (
                          <Alert
                            variant="outlined"
                            severity="error"
                            sx={{
                              borderRadius: "8px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              alignSelf: "stretch",
                            }}
                          >
                            <Typography variant="body2">
                              An error occurred during account confirmation.
                            </Typography>
                          </Alert>
                        )}

                        {signInMutation.isError && (
                          <Alert
                            variant="outlined"
                            severity="error"
                            sx={{
                              borderRadius: "8px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              alignSelf: "stretch",
                            }}
                          >
                            <Typography variant="body2">
                              {getErrorMessage(signInMutation.error)}
                            </Typography>
                          </Alert>
                        )}

                        {convertToHubUserMutation.isError && (
                          <Alert
                            variant="outlined"
                            severity="error"
                            sx={{
                              borderRadius: "8px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              alignSelf: "stretch",
                            }}
                          >
                            <Typography variant="body2">
                              {(convertToHubUserMutation.error as Error)
                                ?.message || "Failed to fetch user information"}
                            </Typography>
                          </Alert>
                        )}

                        <Button
                          id="sign-in"
                          variant="contained"
                          type="submit"
                          disabled={signInMutation.isLoading}
                          fullWidth
                          sx={{ borderRadius: "8px", height: "36px" }}
                          children="Sign In"
                        />
                      </>
                    )}

                    <ButtonGroup
                      variant="text"
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        alignSelf: "stretch",
                        borderRadius: "4px",
                      }}
                      aria-label="Basic button group"
                    >
                      <Button
                        LinkComponent={Link}
                        size="small"
                        color="primary"
                        href={Routes.signUp}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "4px",

                          padding: "4px 5px",
                          gap: "8px",
                          fontSize: "12px",
                        }}
                      >
                        Sign up
                      </Button>

                      <Button
                        LinkComponent={Link}
                        size="small"
                        color="primary"
                        href={Routes.resetPassword}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "4px",
                          padding: "4px 5px",
                          gap: "8px",
                          fontSize: "12px",
                        }}
                      >
                        Password Reset
                      </Button>

                      <Button
                        LinkComponent={Link}
                        size="small"
                        color="primary"
                        href={Routes.resendVerification}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "4px",

                          padding: "4px 5px",
                          gap: "8px",
                          fontSize: "12px",
                        }}
                      >
                        Resend Verification
                      </Button>
                    </ButtonGroup>
                  </CardContent>
                </Card>
              </Box>
            </Form>
          )}
        </Formik>
      )}

      {mfaRequired && (
        <MfaLogin
          onSubmit={async (cognitoUser) => {
            convertToHubUserMutation.mutate(cognitoUser);
          }}
          user={user}
        />
      )}
    </>
  );
};

const customStyle: SxProps<Theme> = {
  [`& .MuiInputLabel-root `]: {
    top: "-5px",
  },

  [`& .MuiInputLabel-shrink `]: {
    top: "0px",
  },
};
